import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ChangePasswordAPI, forgetPasswordAPI, loginAPI, signupAPI } from "../../api/auth/AuthApi";
import { getAdminUserAPI } from "../../api/UserManagementApi";
import { getTokenPayload, saveToken } from "../../../token/token";
import { openEdgeSession } from "../../../util/openEdgeSession";
import { getCompanyVerification } from '../../../utils/companyVerification';

const getInitialHasCompanyVerified = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const storedValue = window.sessionStorage.getItem("hasCompanyVerified");

  if (storedValue === null) {
    return null;
  }

  return storedValue === "true";
};

const toVerificationState = (value) => {
  if (value === null || typeof value === "undefined") {
    return null;
  }

  return value === true || value === "true";
};

const applyCompanyVerification = (state, value) => {
  const verified = toVerificationState(value);
  state.hasCompanyVerified = verified;
  if (typeof window !== 'undefined') {
    if (verified === null) window.sessionStorage.removeItem('hasCompanyVerified');
    else window.sessionStorage.setItem('hasCompanyVerified', String(verified));
  }
};

const logUserDetail = async (userId) => {
  if (!userId) return;

  try {
    const userDetailResponse = await getAdminUserAPI({ userId });
    console.log("get-user-detail response", userDetailResponse);
  } catch (userDetailError) {
    console.log("get-user-detail error", userDetailError);
  }
};

const initialState = {
  login: null,
  loading: null,
  error: {},
  reseting: false,
  hasCompanyVerified: getInitialHasCompanyVerified(),
  verificationRequestId: null,
};

export const singup = createAsyncThunk(
  "auth/singup",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await signupAPI(payload);
      window.location = "/";
      return response;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message || err?.data?.message,
      });
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await loginAPI(payload);
      if (response.data.success) {
        const { accessToken } = response?.data?.response || {};
        saveToken(accessToken);
        const tokenPayload = getTokenPayload("token");
        await openEdgeSession(
          process.env.REACT_APP_CLOUDFRONT_DOMAIN,
          accessToken
        );
        window.sessionStorage.removeItem("hasCompanyVerified");
        const userId =
          tokenPayload?.userId ??
          tokenPayload?.sub ??
          tokenPayload?.id ??
          response?.data?.response?.userId ??
          response?.data?.response?.id ??
          payload?.userId;
        if (userId) {
          window.sessionStorage.setItem("loginUserId", String(userId));
        }
        await logUserDetail(userId);
        const roleName =
          tokenPayload?.roleName ??
          tokenPayload?.rolename ??
          tokenPayload?.role ??
          response?.data?.response?.roleName ??
          response?.data?.response?.rolename ??
          response?.data?.response?.role;
        const isSellerLikeRole = ["SELLER", "STAFF"].includes(roleName);
        const isAdminLikeRole = ["ADMIN", "SUPERADMIN"].includes(roleName);
        window.location =
          isSellerLikeRole && Boolean(tokenPayload?.isNewUser)
            ? "/admin/home"
            : isSellerLikeRole && Boolean(!tokenPayload?.isNewUser)
              ? "/admin/dashboard"
              : isAdminLikeRole
                ? "/admin/seller-tracking-dashboard"
                : "/admin";

        return response;
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.log("err", err)
      return rejectWithValue({
        errorMessage: err?.message || err?.data?.message,
      });
    }
  }
);

export const forgetPassword = createAsyncThunk(
  "auth/forgetPassword",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await forgetPasswordAPI(payload);
      return response;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const updateChangePassword = createAsyncThunk(
  "auth/updateChangePassword",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await ChangePasswordAPI(payload);
      if (response.status === 200) {
        window.location = "/login";
      }
      if (response.response.data.status === 500) {
        return response.response.data;
      }
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setHasCompanyVerified: (state, action) => {
      applyCompanyVerification(state, action.payload);
    }
  },
  extraReducers: (builder) => {
    // Every fresh company lookup must update the flag used by page access.
    // Action types avoid a circular dependency through the API/store modules.
    builder.addCase('companyBrand/getCompany/pending', (state, action) => {
      state.verificationRequestId = action.meta.requestId;
    });
    builder.addCase('companyBrand/getCompany/fulfilled', (state, action) => {
      if (state.verificationRequestId !== action.meta.requestId) return;
      applyCompanyVerification(state, getCompanyVerification(action.payload?.response));
    });
    //login user
    builder.addCase(login.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.login = action.payload.data.response.accessToken;
      saveToken(action.payload.data.response.accessToken);
      state.loading = false;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    //login user
    builder.addCase(forgetPassword.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(forgetPassword.fulfilled, (state, action) => {
      // state.login = action.payload.data.response.accessToken;
      state.loading = false;
    });
    builder.addCase(forgetPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    //update password
    builder.addCase(updateChangePassword.pending, (state) => {
      state.reseting = true;
    });
    builder.addCase(updateChangePassword.fulfilled, (state, action) => {
      state.reseting = false;
    });
    builder.addCase(updateChangePassword.rejected, (state) => {
      state.reseting = false;
    });

    //signup user
    builder.addCase(singup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(singup.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(singup.rejected, (state) => {
      state.loading = false;
    });
  },
});

export default authSlice.reducer;
export const { setHasCompanyVerified } = authSlice.actions;
